interface RequestOptions {
  method?: string;
  timeout?: number;
  withCredentials?: boolean;
  headers?: Record<string, string>;
  data?: string | null;
}

interface ExtendedPromise<T> extends Promise<T> {
  json: <U = any>(executor?: (result: U) => void) => Promise<U>;
  headers: (executor?: (headers: string) => void) => Promise<T>;
}

function addHeaders(request: XMLHttpRequest, options: RequestOptions): void {
  const headers = options.headers || {};
  for (const header in headers) {
    request.setRequestHeader(header, headers[header]);
  }
}

function createRequest(url: string, options: RequestOptions): XMLHttpRequest {
  const request = new XMLHttpRequest();
  const { method = 'GET', timeout = 0 } = options;
  request.open(method, url);
  request.timeout = timeout;
  request.withCredentials = options.withCredentials || false;
  addHeaders(request, options);
  return request;
}

function addHandlers(
  request: XMLHttpRequest,
  resolve: (value: string) => void,
  reject: (reason: any) => void
): void {
  request.onload = function() {
    if (request.status >= 200 && request.status < 400) {
      resolve(request.responseText);
    } else {
      const error: any = Error(
        request.statusText || 'Unknown failure; possibly CORS'
      );
      error.status = request.status;
      reject(error);
    }
  };
  request.ontimeout = function(e) {
    reject(e);
  };
  request.onerror = function(e) {
    reject(e);
  };
  request.onabort = function(e) {
    reject(e);
  };
}

function load(url: string, options: RequestOptions): ExtendedPromise<string> {
  const request = createRequest(url, options);

  const promise = new Promise<string>((resolve, reject) => {
    addHandlers(request, resolve, reject);
    request.send(options.data);
  }) as ExtendedPromise<string>;

  function headers(executor?: (headers: string) => void): Promise<string> {
    return new Promise((resolve, reject) => {
      const exec = executor || resolve;
      request.onreadystatechange = function() {
        if (this.readyState === this.HEADERS_RECEIVED) {
          exec(request.getAllResponseHeaders());
        }
      };
      promise.then(resolve);
      promise.catch(reject);
    });
  }

  function json<U = any>(executor?: (result: U) => void): Promise<U> {
    return new Promise((resolve, reject) => {
      promise
        .then(JSON.parse)
        .then(executor || resolve)
        .catch(reject);
    });
  }

  promise.json = json;
  promise.headers = headers;

  return promise;
}

const ajaxPromises = {
  get(url: string, options: RequestOptions = {}): ExtendedPromise<string> {
    options.method = 'GET';
    options.data = null;
    return load(url, options);
  },

  postJson(
    url: string,
    data: Record<string, any>,
    options: RequestOptions = {}
  ): ExtendedPromise<string> {
    options.method = 'POST';
    options.headers = options.headers || {};
    options.headers['Content-Type'] = 'application/json; charset=UTF-8';
    options.data = JSON.stringify(data);
    return load(url, options);
  },

  waitPromiseOrExecute<T, R>(
    promiseOrResult: Promise<T> | T,
    callback: (result: T) => R,
    errorCallback?: (error: any) => R
  ): R | Promise<R> | undefined {
    try {
      if (promiseOrResult && (promiseOrResult as any).then) {
        return (promiseOrResult as Promise<T>)
          .then(result => {
            return callback(result);
          })
          .catch(error => {
            return errorCallback && errorCallback(error);
          }) as Promise<R>;
      } else {
        return callback(promiseOrResult as T);
      }
    } catch (error) {
      return errorCallback && errorCallback(error);
    }
  }
};

export default ajaxPromises;
